
import React, { useState, useEffect } from 'react';
import { X, Upload, Link as LinkIcon, Image as ImageIcon, Check, FileCheck, RefreshCw, Loader } from 'lucide-react';
import { uploadFile } from '../services/storageService';

interface ImagePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (url: string | string[]) => void;
  initialUrl?: string;
  title?: string;
  contextName?: string;
  allowMultiple?: boolean;
}

const ImagePickerModal: React.FC<ImagePickerModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialUrl = '', 
  title = 'Edycja Zdjęcia',
  contextName = '',
  allowMultiple = false
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload');
  const [urlInput, setUrlInput] = useState('');
  
  const [uploadedFiles, setUploadedFiles] = useState<{url: string, name: string, size: string}[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setUploadError(null);
      if (initialUrl && !initialUrl.startsWith('data:')) {
          setUrlInput(initialUrl);
          // Don't auto-switch tab if empty, but here we can
          if(initialUrl.length > 5) setActiveTab('url');
      } else {
          setActiveTab('upload');
      }
      setUploadedFiles([]);
    }
  }, [isOpen, initialUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setIsProcessing(true);
      setUploadError(null);
      
      const newFiles: typeof uploadedFiles = [];
      
      // Upload sequentially
      for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const result = await uploadFile(file);
          
          if (result.success && result.url) {
              newFiles.push({
                  url: result.url,
                  name: file.name,
                  size: (file.size / 1024).toFixed(1) + ' KB'
              });
          } else {
              setUploadError(`Błąd wgrywania ${file.name}: ${result.error}`);
          }
      }

      if (allowMultiple) {
          setUploadedFiles(prev => [...prev, ...newFiles]);
      } else if (newFiles.length > 0) {
          setUploadedFiles([newFiles[0]]);
      }
      
      setIsProcessing(false);
    }
  };

  const handleSave = () => {
    if (activeTab === 'url') {
       onSave(urlInput);
    } else {
       if (uploadedFiles.length === 0) return;
       
       if (allowMultiple) {
           onSave(uploadedFiles.map(f => f.url));
       } else {
           onSave(uploadedFiles[0].url);
       }
    }
    onClose();
  };

  const removeFile = (index: number) => {
      setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
       <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="p-4 border-b flex justify-between items-center bg-gray-50">
             <h3 className="font-bold text-gray-800 flex items-center gap-2">
               <ImageIcon size={20} className="text-blue-600"/>
               {title}
             </h3>
             <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200">
               <X size={20} />
             </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b">
            <button 
              onClick={() => setActiveTab('upload')}
              className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'upload' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
            >
              <Upload size={16} /> Wybierz z dysku (CMS)
            </button>
            <button 
              onClick={() => setActiveTab('url')}
              className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'url' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
            >
              <LinkIcon size={16} /> Link (URL)
            </button>
          </div>

          {/* Content */}
          <div className="p-6 flex-1 overflow-y-auto bg-gray-50/50">
             {activeTab === 'upload' ? (
                <div className="space-y-6">
                  {/* Dropzone */}
                  <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all group relative overflow-hidden ${isProcessing ? 'border-gray-300 bg-gray-100 cursor-not-allowed' : 'border-blue-300 bg-blue-50 hover:bg-blue-100'}`}>
                    <div className="flex flex-col items-center justify-center pt-5 pb-6 z-10">
                        {isProcessing ? (
                            <div className="flex flex-col items-center text-blue-600">
                                <Loader className="animate-spin w-8 h-8 mb-2" />
                                <span className="text-xs font-bold">Wysyłanie na serwer...</span>
                            </div>
                        ) : (
                            <>
                                <div className="bg-white p-2 rounded-full shadow-sm mb-2 group-hover:scale-110 transition-transform">
                                <Upload className="w-6 h-6 text-blue-500" />
                                </div>
                                <p className="mb-1 text-sm text-blue-700 font-bold">
                                    {allowMultiple ? 'Dodaj zdjęcia (Multi)' : 'Wybierz zdjęcie'}
                                </p>
                                <p className="text-xs text-blue-400">JPG, PNG, WebP (Max 5MB)</p>
                            </>
                        )}
                    </div>
                    <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*" 
                        multiple={allowMultiple} 
                        onChange={handleFileChange} 
                        disabled={isProcessing}
                    />
                  </label>
                  
                  {uploadError && <p className="text-red-500 text-xs font-bold text-center bg-red-50 p-2 rounded">{uploadError}</p>}

                  {/* File List */}
                  {uploadedFiles.length > 0 && (
                      <div className="space-y-2">
                          <div className="flex justify-between items-end mb-2">
                              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Wgrane pliki ({uploadedFiles.length})</h4>
                          </div>
                          
                          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-60 overflow-y-auto">
                              {uploadedFiles.map((file, idx) => (
                                  <div key={idx} className="p-3 flex gap-3 items-center group">
                                      <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0 border border-gray-200 checkerboard-bg">
                                          <img src={file.url} className="w-full h-full object-cover" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                              <span className="text-sm font-bold text-gray-800 truncate">{file.name}</span>
                                              <FileCheck size={12} className="text-green-500"/>
                                          </div>
                                          <p className="text-[10px] text-gray-400">{file.size}</p>
                                      </div>
                                      <button 
                                        onClick={() => removeFile(idx)}
                                        className="text-gray-400 hover:text-red-500 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                          <X size={16} />
                                      </button>
                                  </div>
                              ))}
                          </div>
                      </div>
                  )}
                </div>
             ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Adres obrazka (URL)</label>
                    <input 
                      autoFocus
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="https://example.com/image.jpg"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                    />
                    <p className="text-xs text-gray-400 mt-2">Wklej bezpośredni link do pliku graficznego.</p>
                  </div>
                  {urlInput && (
                      <div className="mt-4">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Podgląd URL</p>
                        <div className="h-32 bg-gray-100 rounded border flex items-center justify-center overflow-hidden">
                            <img src={urlInput} className="h-full object-contain" onError={(e) => e.currentTarget.style.display='none'}/>
                        </div>
                      </div>
                  )}
                </div>
             )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
             <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg font-medium text-sm transition-colors">
               Anuluj
             </button>
             <button 
                onClick={handleSave} 
                disabled={activeTab === 'upload' && uploadedFiles.length === 0}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm shadow-md transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               <Check size={16} /> Użyj Wybranych
             </button>
          </div>
       </div>
    </div>
  );
};

export default ImagePickerModal;
